/**
 * Minimal subset of the Auth0 SPA client surface required by
 * {@link executeWithAuthRetry}. Declared with permissive `(...args: any[])`
 * signatures so a concrete `Auth0VueClient` flows through without friction,
 * while remaining simple to mock in tests.
 */
export interface AuthRetryClient {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  getAccessTokenSilently: (...args: any[]) => Promise<any>
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

function isHttp401(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === 'object' &&
    (err as { status?: unknown }).status === 401
  )
}

export interface ExecuteWithAuthRetryDeps {
  /** Lazy accessor for the Auth0 client. Only called when a retry is needed. */
  getAuthClient: () => AuthRetryClient
  /** Called when the silent refresh itself fails — kicks the user to sign-in. */
  forceReauthentication: () => void
}

/**
 * Module-level singleton promise for an in-flight token refresh. When two
 * or more requests return 401 concurrently, they all await this one promise
 * instead of racing to call `getAccessTokenSilently` in parallel. Auth0's
 * SDK may or may not dedupe internally depending on version — don't bet on
 * undocumented SDK internals.
 */
let refreshInFlight: Promise<unknown> | null = null

/**
 * Test-only helper to reset the module-level singleton between test runs.
 * Never call this from production code.
 */
export function _resetAuthRetrySingletonForTests(): void {
  refreshInFlight = null
}

/**
 * Run `action` and, if it throws with HTTP 401, force-refresh the access token
 * via `deps.getAuthClient()` and retry the action exactly once. If the refresh
 * itself fails, invoke `deps.forceReauthentication()` (which typically navigates
 * the user to a fresh login) and rethrow the original 401 so the in-flight
 * request fails cleanly while the browser is already unwinding.
 *
 * Concurrent 401s share a single token-refresh call via a module-level
 * `refreshInFlight` promise — see {@link refreshInFlight}.
 *
 * Both dependency factories are lazy — they're only invoked when a retry is
 * required, and their throws are tolerated (so `executeWithAuthRetry` is safe
 * to call before Auth0 is initialized).
 */
export async function executeWithAuthRetry<T>(
  action: () => Promise<T>,
  deps: ExecuteWithAuthRetryDeps
): Promise<T> {
  try {
    return await action()
  } catch (err) {
    if (!isHttp401(err)) throw err

    try {
      if (refreshInFlight === null) {
        const authClient = deps.getAuthClient()
        refreshInFlight = authClient
          .getAccessTokenSilently({ cacheMode: 'off' })
          .finally(() => {
            refreshInFlight = null
          })
      }
      await refreshInFlight
    } catch {
      try {
        deps.forceReauthentication()
      } catch {
        /* `forceReauthentication` is best-effort — if it throws we can't
         * recover further. The original 401 still rethrows below. */
      }
      throw err
    }
    return await action()
  }
}
